import { useState } from "react";
import useComments, { comment_types, comment_sort_fields } from "@core/helpers/client/hooks/useComments";
import { toast_configs } from "@core/constants/toast_configs";
import { show_mutation_error_toast } from "@/helpers/mutationErrors";
import useDate from "@core/helpers/client/hooks/useDate";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { openAuthModal } from "@/hooks/useAuthModal";
import LoadingAll from "@/components/LoadingAll";
import Button from "@/components/ui/button";
import ListBox from '@/components/ui/listBox';
import { ChevronDown, PencilLine } from 'lucide-react';
import Collapse from "@/components/ui/Collapse";
import ModalComponent from "@/components/ui/ModalComponent";
import TextArea from "../../ui/TextareaInput";
import { filter_confirmed_comments, is_confirmed_comment } from "@/helpers/comments";
import { comments_with_replies } from "@/gql-mockups/comments";
import CommentBadge, { get_comment_author_name } from "@/components/CommentBadge";

const Questions = ({ data }) => {

    const {
        comment_form,
        on_change_comment_form,
        state,
        create_comment_handler,
        load_more_comments_handler,
        set_reply_to,
        reply_comment_handler,
        sort,
        set_sort
    } = useComments(
        { model_id: data?.id, model_name: "ProductModel", model_type: "1" },
        { is_with_replies: true, lazy_load: false, limit: 3, comment_type: comment_types?.forum, initial_sort: comment_sort_fields.newest, comments_mockup: comments_with_replies }
    );

    const { comments, create_comment_loading, has_more, get_comments_loading, pagination, reply_to } = state;
    const { get_date_client } = useDate();
    const { is_auth } = useAuthContext();

    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

    // پرسش/پاسخ تازه ثبت‌شده تا زمان تایید ادمین نباید نمایش داده شود
    const visible_questions = filter_confirmed_comments(comments);

    // نوار مرتب‌سازی هنگام بارگذاری مجدد بعد از تغییر ترتیب هم باید بماند
    const show_sort = visible_questions.length > 0 || (get_comments_loading && data?.forums_count > 0);

    const isDisabled = !comment_form?.text?.trim();


    // کاربر مهمان: به جای پیام خطا، صفحه‌ی ورود نمایش داده می‌شود
    const authError = () => openAuthModal();

    const submitHandler = (e, args) => {

        if (reply_to?.id?.length > 0) {
            reply_comment_handler(e, args)
        } else {
            create_comment_handler(e, args);
        }

        setIsReviewModalOpen(false);
    };

    const openNewComment = ({ comment }) => {
        if (!is_auth) return authError();

        if (comment?.id?.length > 0) {
            set_reply_to(comment)
        }
        else (set_reply_to(null))
        setIsReviewModalOpen(true);
    };

    const renderDate = (date) =>
        `${get_date_client(date)}`;

    return (
        <div className="flex flex-col lg:flex-row items-start justify-between gap-4">

            {/* SIDEBAR */}
            <div className="w-full lg:w-[30%] xl:w-[25%] lg:sticky lg:top-[calc(var(--header-height)+60px)]">
                <h2 className="font-bold lg:text-xl text-jmt-main lg:mb-5">پرسش‌ها</h2>
                <p className="hidden lg:block text-sm text-jmt-main mt-4">
                    {data?.forums_count || 0} پرسش
                </p>
                <p className="hidden lg:block text-xs text-jmt-main mt-4">
                    شما هم درباره این کالا پرسش ثبت کنید
                </p>

                <Button
                    text={"ثبت پرسش"}
                    onClick={openNewComment}
                    color_type="primary"
                    variant="containd"
                    className="w-full hidden lg:flex lg:mt-4"
                />

                <div className='flex items-center justify-between lg:hidden'>
                    <p className="text-sm text-jmt-main mt-4">
                        {data?.forums_count || 0} پرسش
                    </p>
                    <Button
                        text={"ثبت پرسش"}
                        onClick={openNewComment}
                        color_type="primary"
                        variant="containd"
                        className="w-1/3 lg:hidden"
                    />
                </div>
            </div>

            {/* COMMENTS */}
            <div className="w-full lg:w-[65%] xl:w-[70%]">

                {show_sort && (
                    <div className="flex items-center justify-between mb-5">
                        <ListBox
                            variant="horizontal"
                            sort={sort}
                            set_sort={set_sort}
                            options={[
                                { name: "جدیدترین", value: comment_sort_fields.newest },
                                { name: "بیشترین پاسخ", value: comment_sort_fields.most_replied },
                            ]}
                        />
                    </div>
                )}

                {visible_questions?.length > 0 ?
                    <>
                        {visible_questions?.map((item, index) => (
                            <div key={item?.id ?? `question-${index}`} className="not-last:border-b border-jmt-neutral-200 p-2 space-y-3" >

                                {/* Question Header */}
                                <div className='flex flex-col space-y-3'>
                                    <div className="flex justify-between items-start">
                                        <div className="text-sm text-jmt-main font-bold leading-7 flex-1">{item?.text}</div>
                                        <div className="text-xs text-jmt-neutral-500 mr-3">{renderDate(item?.updatedAt)}</div>
                                    </div>

                                </div>

                                {/* Answer Section */}
                                <Collapse
                                    collapsedHeight={130}
                                    expandText="مشاهده پاسخ های دیگر"
                                    collapseText="بستن"
                                    expandButtonClassName="text-jmt-primary-800 text-xs font-bold mt-4"
                                    collapseButtonClassName="text-jmt-primary-800 text-xs font-bold mt-4"
                                >
                                    <div className='space-y-3'>
                                        {item.replies?.filter(is_confirmed_comment)?.map((reply, reply_index) => (
                                            <div key={reply?.id ?? `question-reply-${reply_index}`}>
                                                <div className="flex items-center gap-2 pb-3">
                                                    <span className="text-sm text-jmt-main">{get_comment_author_name(reply, reply?.user?.full_name || reply?.user?.nickname)}</span>
                                                    <CommentBadge comment={reply} />
                                                    <span className='text-jmt-neutral-200'>|</span>
                                                    <span className="text-xs text-jmt-neutral-500">{renderDate(reply.updatedAt)}</span>
                                                </div>
                                                <p className="text-sm text-jmt-main pr-8">{reply?.text}</p>

                                            </div>
                                        ))}

                                    </div>

                                    <Button
                                        text={" ثبت پاسخ "}
                                        start_icon={<PencilLine size={14} />}
                                        color_type="tertiary"
                                        className="whitespace-nowrap p-2 gap-1"
                                        onClick={() => openNewComment({ comment: item })}
                                    />
                                </Collapse>
                            </div>
                        ))}
                    </> : get_comments_loading ?
                        null :
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <svg className="w-10 h-10 lg:w-[65px] lg:h-[65px]" viewBox="0 0 130 130" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M49.2374 48.7507C50.5109 45.1306 53.0245 42.078 56.333 40.1335C59.6415 38.1891 63.5315 37.4783 67.3138 38.1271C71.0962 38.7759 74.5269 40.7423 76.9983 43.6782C79.4697 46.614 80.8223 50.3298 80.8166 54.1674C80.8166 65.0007 64.5666 70.4174 64.5666 70.4174M65.0001 92.0836H65.0543M16.2071 88.5191C17.0036 90.5283 17.1809 92.7297 16.7163 94.8404L10.9475 112.661C10.7616 113.565 10.8097 114.501 11.0871 115.381C11.3646 116.261 11.8622 117.056 12.5328 117.689C13.2034 118.323 14.0248 118.775 14.919 119.002C15.8133 119.229 16.7507 119.225 17.6425 118.988L36.1296 113.582C38.1214 113.187 40.1841 113.36 42.0825 114.08C53.6492 119.482 66.752 120.625 79.0792 117.307C91.4063 113.99 102.166 106.425 109.459 95.9474C116.752 85.47 120.11 72.7534 118.941 60.0413C117.772 47.3291 112.151 35.4383 103.069 26.4669C93.9874 17.4954 82.0288 12.0198 69.3033 11.0061C56.5779 9.99239 43.9033 13.5058 33.5158 20.9264C23.1283 28.347 15.6954 39.1979 12.5286 51.5647C9.36183 63.9314 10.6646 77.0193 16.2071 88.5191Z" stroke="#08535B" strokeWidth="3.83" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div className="flex flex-col justify-center items-center text-sm text-jmt-main text-center">
                            <span className="text-center">اینجا هنوز پرسشی دیده نمیشه...</span>
                            <span className="text-center">تو اولین پرسش رو ثبت کن</span>
                        </div>
                    </div>
                }
                {get_comments_loading ?
                    <div className="mt-3">
                        <LoadingAll className="h-5!" />
                    </div>
                    : has_more && visible_questions?.length > 0 && (
                        <div className="flex items-center justify-start mt-4">

                            <Button
                                text={pagination?.total - visible_questions?.length > 0 ?
                                    <span className="font-bold text-xs">
                                        {pagination?.total - visible_questions?.length} پرسش دیگر
                                    </span> : ""}
                                onClick={load_more_comments_handler}
                                end_icon={<ChevronDown size={16} />}
                                color_type="tertiary"
                                className="whitespace-nowrap"
                            />

                        </div>
                    )}
            </div>

            {/* REVIEW MODAL */}
            <ModalComponent
                isOpen={isReviewModalOpen}
                onClose={() => setIsReviewModalOpen(false)}
                title={reply_to?.id?.length > 0 ? "به این پرسش پاسخ دهید" : "ثبت پرسش"}
                size="md"
                full={true}
                footer={
                    <Button
                        text={reply_to?.id?.length > 0 ? "ثبت پاسخ" : "ثبت پرسش"}
                        color_type="primary"
                        variant="contained"
                        className="w-full"
                        disabled={isDisabled || create_comment_loading}
                        onClick={(e) =>
                            submitHandler(e, {
                                success_callback: () => {
                                    toast_configs(`${reply_to?.id?.length > 0 ? "پاسخ" : "پرسش"} شما ثبت شد و پس از بررسی نمایش داده خواهد شد`, "success", {
                                        className: "custome_toastify",
                                        position: "bottom-center",
                                        autoClose: 2000,
                                    })
                                },
                                error_callback: show_mutation_error_toast,
                                not_auth_callback: authError,
                            })}
                    />
                }
            >
                <div className="max-h-[540px] overflow-y-auto space-y-4">
                    <div className="text-sm text-jmt-neutral-700">{reply_to?.id?.length > 0 ? reply_to?.text : ""} </div>

                    <TextArea
                        name="text"
                        label={reply_to?.id?.length > 0 ? "متن پاسخ" : "متن پرسش"}
                        required
                        value={comment_form?.text || ""}
                        onChange={on_change_comment_form}
                        rows={5}
                        placeholder={`${reply_to?.id?.length > 0 ? "پاسخ" : "پرسش"} خود را در مورد این محصول بنویسید...`}
                        className="w-[96%]! mx-auto! resize-none!"
                    />

                    <p className="text-xs text-jmt-main leading-6">
                        ثبت {reply_to?.id?.length > 0 ? "پاسخ" : "پرسش"} به معنی موافقت شما با{" "}
                        <a href="/privacy-policy/" target="_blank" className="text-jmt-primary-700 hover:underline">
                            قوانین انتشار
                        </a>{" "}
                        نظرات است.
                    </p>

                </div>
            </ModalComponent>
        </div>
    );
};

export default Questions;
